import React, { useEffect, useState } from 'react';
import { CheckCircle, X } from 'lucide-react';
import { supportApi } from '../../utils/issueReporter/supportApi';

import { RubyGemMark } from '../RubyGemMark';
/**
 * Shows a slim emerald banner at the top of the page when the user opens the
 * app at a URL containing `?retest=<issueId>`. Lets the reporter confirm the
 * fix worked or say it's still broken.
 *
 * Mounted by IssueReporterRoot so it's available globally.
 */
const TestFixBanner: React.FC = () => {
  const [issue, setIssue] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const sync = async () => {
      const id = readRetestIdFromHash();
      if (!id) {
        setIssue(null);
        return;
      }
      if (issue?.id === id) return;
      try {
        const res = await supportApi.getIssue(id);
        setIssue(res.issue);
      } catch (e) {
        console.warn('[testFix] load failed', e);
      }
    };
    sync();
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, [issue?.id]);

  if (!issue || dismissed) return null;
  if (issue.status !== 'awaiting_retest') return null;

  const confirm = async () => {
    setBusy(true);
    setErr(null);
    try {
      await supportApi.confirmFix(issue.id);
      setDismissed(true);
      stripRetestFromHash();
    } catch (e: any) {
      setErr(e?.message || 'Failed to confirm');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!rejectNote.trim()) {
      setShowRejectInput(true);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await supportApi.rejectFix(issue.id, rejectNote.trim());
      setDismissed(true);
      stripRetestFromHash();
    } catch (e: any) {
      setErr(e?.message || 'Failed to report');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sticky top-0 z-[9980] bg-emerald-900/95 border-b border-emerald-700/60 px-4 py-2.5 backdrop-blur-sm">
      <div className="max-w-5xl mx-auto flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
            <CheckCircle size={14} className="text-white" />
          </div>
          <div className="text-xs flex-1 min-w-0">
            <div className="text-emerald-100 font-medium truncate">
              Ticket #{issue.id?.slice(-8)} — {issue.title}
            </div>
            <div className="text-emerald-200/80">
              We think we fixed it. Does it work for you now?
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showRejectInput && (
            <input
              type="text"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="What's still broken?"
              className="px-2 py-1 text-xs rounded-md bg-emerald-950 border border-emerald-700 text-white placeholder-emerald-300 w-48 focus:outline-none focus:border-emerald-500"
            />
          )}
          <button
            onClick={reject}
            disabled={busy}
            className="px-3 py-1.5 text-xs rounded-lg border border-emerald-600/60 text-emerald-100 hover:bg-emerald-900 disabled:opacity-50"
          >
            Still broken
          </button>
          <button
            onClick={confirm}
            disabled={busy}
            className="px-3 py-1.5 text-xs rounded-lg bg-white text-black font-medium hover:bg-neutral-200 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <RubyGemMark size={12} drawing color="currentColor" /> : <CheckCircle size={12} />}
            Confirm Fixed
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 text-emerald-200/70 hover:text-white"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
      {err && (
        <div className="max-w-5xl mx-auto mt-2 text-[11px] text-red-200">{err}</div>
      )}
    </div>
  );
};

function readRetestIdFromHash(): string | null {
  const hash = window.location.hash || '';
  const q = hash.indexOf('?');
  if (q === -1) return null;
  const params = new URLSearchParams(hash.slice(q + 1));
  return params.get('retest');
}

function stripRetestFromHash(): void {
  const hash = window.location.hash || '';
  const q = hash.indexOf('?');
  if (q === -1) return;
  const route = hash.slice(0, q);
  const params = new URLSearchParams(hash.slice(q + 1));
  params.delete('retest');
  const remaining = params.toString();
  window.location.hash = remaining ? `${route}?${remaining}` : route;
}

export default TestFixBanner;
